import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Container,
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
    InputAdornment,
    IconButton,
    Fade,
    Grid,
    Link,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    MailOutline
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
// Import the actual Auth context hook
import { useAuth } from '../context/AuthContext'; 
import AuthLayout from '../components/AuthLayout';
import { isKranawetterPortal } from '../utils/portalHost';
import { companyUserHome } from '../utils/phoneHome';

function LoginPage() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
    const { login, completeLoginCode, resendLoginCode } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [pendingToken, setPendingToken] = useState('');
    const [emailHint, setEmailHint] = useState('');
    const [code, setCode] = useState('');
    const [resent, setResent] = useState(false);
    const buttonRef = useRef(null);
    const submittedCode = useRef('');    

    // Load remembered email on mount
    useEffect(() => {
        const rememberedEmail = localStorage.getItem('hammer-lane-tech-remembered-email');
        if (rememberedEmail) {
            setEmail(rememberedEmail);
            setRememberMe(true);
        }
    }, []);


    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault(); // Prevent blur on click
    };

    const handleRememberMeChange = (event) => {
        const checked = event.target.checked;
        setRememberMe(checked);
        
        // If unchecked, remove remembered email immediately
        if (!checked) {
            localStorage.removeItem('hammer-lane-tech-remembered-email');
        }
    };

    // Enhanced login with tracking
    const handleLogin = async (e) => {
        if (e) e.preventDefault();
        setError('');
        setLoading(true);

        try {
            // Use AuthContext login function which handles the API call
            const result = await login(email, password, rememberMe);
            
            if (result.requiresTwoFactor) {
                setPendingToken(result.pendingToken);
                setEmailHint(result.emailHint || 'your email');
                setCode('');
                submittedCode.current = '';
                setResent(false);
                setLoading(false);
                return;
            }

            if (result.success) {
                // Handle remember me functionality
                if (rememberMe) {
                    localStorage.setItem('hammer-lane-tech-remembered-email', email);
                } else {
                    localStorage.removeItem('hammer-lane-tech-remembered-email');
                }
                
                // Check if password change is required
                if (result.requirePasswordChange) {
                    navigate('/temporary-password-reset');
                } else {
                    navigate(companyUserHome(result.role, isPhone));
                }
            } else {
                // Handle login failure
                if (result.errorType === 'password_reset_required') {
                    // Special handling for password reset required
                    setError(result.error);
                    setLoading(false);
                } else {
                    const errorMsg = result.error || 'Invalid credentials. Please check your email and password.';
                    setError(errorMsg);
                    setLoading(false);
                }
            }
        } catch (err) {
            // Fallback error handling for unexpected errors
            const errorMessage = 'An unexpected error occurred. Please try again.';
            setError(errorMessage);
            console.error('Unexpected login error:', err);
            setLoading(false);
        }
    };

    const finishCode = async (nextCode) => {
        if (!pendingToken || nextCode.length !== 6 || loading) return;
        setError('');
        setLoading(true);
        try {
            const result = await completeLoginCode(pendingToken, nextCode);
            if (result.success) {
                if (rememberMe) {
                    localStorage.setItem('hammer-lane-tech-remembered-email', email);
                } else {
                    localStorage.removeItem('hammer-lane-tech-remembered-email');
                }
                navigate(result.requirePasswordChange ? '/temporary-password-reset' : companyUserHome(result.role, isPhone));
                return;
            }
            setError(result.error || 'Incorrect code');
            setLoading(false);
        } catch (err) {
            setError('An unexpected error occurred. Please try again.');
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!pendingToken) return;
        if (code.length !== 6 || submittedCode.current === code) return;
        submittedCode.current = code;
        finishCode(code);
        // finishCode closes over the latest token and remember-me choice.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code, pendingToken]);

    const handleResendCode = async () => {
        setError('');
        setLoading(true);
        try {
            const next = await resendLoginCode(pendingToken);
            setPendingToken(next.pendingToken);
            if (next.emailHint) setEmailHint(next.emailHint);
            setCode('');
            submittedCode.current = '';
            setResent(true);
        } catch (err) {
            setError(err.response?.data?.message || 'Could not send a new code');
        } finally {
            setLoading(false);
        }
    };

    const backToLogin = () => {
        setPendingToken('');
        setCode('');
        setError('');
        setResent(false);
        submittedCode.current = '';
        setLoading(false);
    };

    const digitsOnly = (value) => value.replace(/\D/g, '').slice(0, 6);

    // Track forgot password click
    const handleForgotPasswordClick = () => {
        // Button click tracking handled by global audit middleware
    };

    return (
        <>
            <Helmet>
                <title>Kranawetter Driver Portal | Sign In</title>
                <meta name="description" content="Kranawetter Driver Portal - Trucking company management software to organize customers, track important truck documents, and convey safety information to drivers." />
                <meta name="keywords" content="trucking, fleet management, truck documents, safety, logistics, transportation management" />
                <meta property="og:title" content="Kranawetter Driver Portal | Trucking Fleet Management Software" />
                <meta property="og:description" content="Streamline your trucking operations with Kranawetter Driver Portal. Manage customers, track truck documentation, and communicate safety information efficiently." />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Kranawetter Driver Portal | Sign In" />
                <meta name="twitter:description" content="Comprehensive trucking management solution for fleet operators and logistics companies." />
            </Helmet>
            
            <AuthLayout>
                <Box 
                    sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        width: '100%'
                    }}
                >
                <Paper 
                    elevation={3} 
                    sx={{ 
                        marginTop: { xs: 2, sm: 4 },
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        padding: { xs: 2, sm: 3 },
                        position: 'relative',
                            maxWidth: pendingToken ? 480 : 420,
                            width: '100%',
                            bgcolor: '#fffbf5'
                    }}
                >
                    {/* Logo */}
                    <Box 
                        component="img"
                        sx={isKranawetterPortal() ? {
                            width: '100%',
                            height: 'auto',
                            objectFit: 'contain',
                            mb: 1.5,
                        } : {
                            height: { xs: 48, sm: 56 },
                            width: 'auto',
                            maxWidth: '80%',
                            objectFit: 'contain',
                            mb: 1.5,
                        }}
                        alt="Kranawetter Driver Portal Logo"
                        src="/hammer.png" 
                    />
                    {pendingToken ? (
                        <Box sx={{ textAlign: 'center', mb: 1 }}>
                            <Box
                                sx={{
                                    width: 52,
                                    height: 52,
                                    mx: 'auto',
                                    mb: 1.5,
                                    borderRadius: '50%',
                                    display: 'grid',
                                    placeItems: 'center',
                                    bgcolor: 'rgba(233, 79, 55, 0.12)',
                                    color: 'primary.main'
                                }}
                            >
                                <MailOutline />
                            </Box>
                            <Typography component="h1" variant="h5" sx={{ fontWeight: 800 }}>
                                Check your inbox
                            </Typography>
                            <Typography color="text.secondary" sx={{ mt: 0.75 }}>
                                We sent a 6-digit code to
                            </Typography>
                            <Typography sx={{ mt: 0.25, fontWeight: 800 }}>
                                {emailHint}
                            </Typography>
                            <Typography sx={{ mt: 0.75, fontSize: 13, fontWeight: 700, color: 'primary.main' }}>
                                Expires in 10 minutes
                            </Typography>
                        </Box>
                    ) : (
                    <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
                        Sign in
                    </Typography>
                    )}
                    
                    {/* Error message - visible when error is not empty */}
                    {error && (
                        <Alert 
                            severity={error.includes('password must be reset') ? 'warning' : 'error'}
                            variant="filled"
                            sx={{ 
                                width: '100%',
                                mb: 1,
                                animation: error.includes('password must be reset') ? 'none' : 'shake 0.5s',
                                '@keyframes shake': {
                                    '0%, 100%': { transform: 'translateX(0)' },
                                    '10%, 30%, 50%, 70%, 90%': { transform: 'translateX(-5px)' },
                                    '20%, 40%, 60%, 80%': { transform: 'translateX(5px)' },
                                }
                            }}
                            onClose={() => setError('')}
                        >
                            {error}
                            {error.includes('password must be reset') && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Didn't receive the email? Check your spam folder or request a new reset link.
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        component={RouterLink}
                                        to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                                        sx={{ 
                                            color: 'inherit',
                                            borderColor: 'inherit',
                                            '&:hover': {
                                                borderColor: 'inherit',
                                                backgroundColor: 'rgba(255,255,255,0.1)'
                                            }
                                        }}
                                    >
                                        Resend Reset Email
                                    </Button>
                                </Box>
                            )}
                        </Alert>
                    )}
                    
                    {resent && !error && pendingToken && (
                        <Alert severity="success" sx={{ width: '100%', mb: 1 }} onClose={() => setResent(false)}>
                            A new code is on the way.
                        </Alert>
                    )}

                    {pendingToken ? (
                        <Box
                            component="form"
                            sx={{ width: '100%' }}
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (code.length === 6) {
                                    submittedCode.current = code;
                                    finishCode(code);
                                }
                            }}
                        >
                            <TextField
                                autoFocus
                                fullWidth
                                value={code}
                                disabled={loading}
                                error={!!error}
                                label="Sign-in code"
                                autoComplete="one-time-code"
                                onPaste={(e) => {
                                    e.preventDefault();
                                    setCode(digitsOnly(e.clipboardData.getData('text')));
                                }}
                                onChange={(e) => setCode(digitsOnly(e.target.value))}
                                helperText="Paste the whole code from the email."
                                inputProps={{
                                    inputMode: 'numeric',
                                    pattern: '[0-9]*',
                                    'aria-label': 'Sign-in code',
                                    style: {
                                        fontSize: 28,
                                        fontWeight: 800,
                                        letterSpacing: '0.28em',
                                        textAlign: 'center',
                                        fontVariantNumeric: 'tabular-nums'
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <MailOutline fontSize="small" sx={{ color: 'text.secondary' }} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Typography align="center" color="text.secondary" sx={{ mt: 1.5, fontSize: 13, fontWeight: 600 }}>
                                {loading ? 'Checking your code…' : 'Paste from the email, or type the 6 digits.'}
                            </Typography>
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="large"
                                disabled={loading || code.length !== 6}
                                sx={{ mt: 2 }}
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign in'}
                            </Button>
                            <Button
                                type="button"
                                fullWidth
                                disabled={loading}
                                onClick={handleResendCode}
                                sx={{ mt: 1, fontWeight: 800 }}
                            >
                                Send a new code
                            </Button>
                            <Button
                                type="button"
                                fullWidth
                                disabled={loading}
                                onClick={backToLogin}
                                sx={{ color: 'text.secondary' }}
                            >
                                Use a different account
                            </Button>
                        </Box>
                    ) : (
                    /* Kranawetter Driver Portal Login Form */
                    <Box 
                        component="form"
                        id="hammer-lane-tech-login-form"
                        sx={{ width: '100%' }}
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleLogin(e);
                        }}
                    >
                        <TextField
                            margin="dense"
                            required
                            fullWidth
                            id="hammer-lane-tech-email-login"
                            label="Email Address"
                            name="hammer-lane-tech-email"
                            autoComplete="username email hammer-lane-tech-email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            variant="outlined"
                            size="small"
                            data-form-type="login"
                            data-site="hammer-lane-tech"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#fffbf5'
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleLogin();
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            required
                            fullWidth
                            name="hammer-lane-tech-password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            id="hammer-lane-tech-password-login"
                            autoComplete="current-password hammer-lane-tech-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            variant="outlined"
                            size="small"
                            data-form-type="login"
                            data-site="hammer-lane-tech"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#fffbf5'
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleLogin();
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            disabled={loading}
                                            size="small"
                                        >
                                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {/* Remember Me & Forgot Password Row */}
                        <Box sx={{ mt: 0.5, width: '100%' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox 
                                            value="remember"
                                            color="primary"
                                            checked={rememberMe}
                                            onChange={handleRememberMeChange}
                                            disabled={loading}
                                            size="small"
                                        />
                                    }
                                    label={<Typography variant="body2">Keep me logged in for 14 days</Typography>}
                                />
                                <Typography variant="body2">
                                    <RouterLink 
                                        to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                        onClick={handleForgotPasswordClick}
                                    >
                                        Forgot password?
                                    </RouterLink>
                                </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4, mt: -0.5 }}>
                                {rememberMe
                                    ? 'Stay signed in for 14 days. Admin email codes are skipped on this browser for 30 days.'
                                    : 'Signs out after 24 hours. Admin accounts get an email code each sign-in.'}
                            </Typography>
                        </Box>
                        <Button
                            ref={buttonRef}
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 1.5, mb: 1, py: 1 }}
                            disabled={loading}
                            size="medium"
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
                        </Button>
                    </Box>
                    )}
                </Paper>
                </Box>
            </AuthLayout>
        </>
    );
}

export default LoginPage; 